import { useEffect } from "react";
import { OG_IMAGE, SITE_NAME } from "@/seo/routeMeta";

const LD_ID = "kr-route-ld";

const upsert = (selector, create, attr, content) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, content);
  return () => el.setAttribute(attr, prev ?? "");
};

const meta = (key, attrName, content) =>
  upsert(
    `meta[${attrName}="${key}"]`,
    () => {
      const m = document.createElement("meta");
      m.setAttribute(attrName, key);
      return m;
    },
    "content",
    content
  );

const setStructuredData = (items) => {
  document.head.querySelectorAll(`script#${LD_ID}`).forEach((s) => s.remove());
  if (!items || items.length === 0) return () => {};
  const script = document.createElement("script");
  script.id = LD_ID;
  script.type = "application/ld+json";
  script.text = JSON.stringify(items.length === 1 ? items[0] : items);
  document.head.appendChild(script);
  return () => script.remove();
};

/**
 * Per-route document metadata (title, description, canonical, robots,
 * Open Graph, Twitter, route-level JSON-LD). Captured by react-snap into
 * each route's pre-rendered HTML when available; scripts/inject-meta.js
 * writes the same values into static HTML at build time regardless.
 * Restores previous values on unmount / route change.
 */
export const usePageMeta = ({
  title,
  description,
  canonical,
  image,
  type,
  noindex,
  structuredData,
}) => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const img = image || OG_IMAGE;

    const restores = [
      meta("description", "name", description),
      meta("robots", "name", noindex ? "noindex, nofollow" : "index, follow"),
      upsert(
        'link[rel="canonical"]',
        () => {
          const l = document.createElement("link");
          l.setAttribute("rel", "canonical");
          return l;
        },
        "href",
        canonical
      ),
      meta("og:type", "property", type || "website"),
      meta("og:site_name", "property", SITE_NAME),
      meta("og:title", "property", title),
      meta("og:description", "property", description),
      meta("og:url", "property", canonical),
      meta("og:image", "property", img),
      meta("twitter:card", "name", "summary_large_image"),
      meta("twitter:title", "name", title),
      meta("twitter:description", "name", description),
      meta("twitter:image", "name", img),
      setStructuredData(structuredData),
    ];

    return () => {
      document.title = prevTitle;
      restores.forEach((r) => r());
    };
    // structuredData is derived from the route; canonical is the stable key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, image, type, noindex]);
};
